/**
 * Resolve a button `borderWidth` setting to a CSS length.
 *
 * Border width is the only button property that historically had no route
 * from the design system to the rendered button. Every other property goes
 * through `getDesignSystemValue` in `Button.styled.ts`, which maps the
 * `"design-system"` sentinel (and empty / legacy values) onto the matching
 * CSS custom property. Width instead used `Number(value) || 1`, so the
 * design system's Border → Width control was cosmetic: `--button-border-width`
 * was written to the document root but nothing ever read it, and any button
 * whose stored width was missing or non-numeric rendered a phantom 1px
 * border even when the design system said 0px.
 *
 * Resolution order:
 *   - an explicit number (`2`, `"2"`, `"2px"`) wins — that is a per-element
 *     override the user set through the settings slider;
 *   - anything else (missing, empty, the `"design-system"` sentinel, or a
 *     stale `var(--…)` reference) defers to the design system variable.
 *
 * ## Why the last resort is 1px and not 0px
 *
 * A design system that predates the button border controls has no
 * `buttonBorderWidth` at all, so `--button-border-width` is never written to
 * the root. The old `Number(value) || 1` gave those buttons a 1px border, and
 * that is what every such story has always rendered. Resolving them to `0px`
 * instead silently removed the border from existing decks the moment this code
 * shipped — reported as a design system that "looks like it was not saved",
 * because the editor drew borderless buttons while the not-yet-redeployed
 * reader bundle still drew the border.
 *
 * So the last resort reproduces the historical default. An explicit `0` still
 * clears the border — that is a value the user or the design system chose, and
 * `parseExplicitBorderWidth` returns it before we ever get here.
 */

const BORDER_WIDTH_VAR = {
  primary: "var(--button-border-width, 1px)",
  // Chained explicitly rather than relying on the secondary → primary
  // fallback written by `resolveDesignSystemVariables`, so the `1px` last
  // resort still applies when neither variable is defined.
  secondary:
    "var(--secondary-button-border-width, var(--button-border-width, 1px))",
} as const;

/**
 * Parse an explicit per-element border width. Returns `null` when the value
 * is not a concrete number and should therefore defer to the design system.
 */
export const parseExplicitBorderWidth = (
  value: string | number | undefined | null,
): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Only plain numbers and px lengths are element-level overrides. A
  // `var(--…)` reference or the `"design-system"` sentinel is not.
  const match = trimmed.match(/^(-?\d*\.?\d+)(px)?$/);
  if (!match) return null;
  const parsed = Number.parseFloat(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Resolve a button `borderWidth` setting to the CSS length to render with.
 * Pass `isSecondary` so a secondary button resolves against the secondary
 * design-system variable.
 */
export const resolveBorderWidthCss = (
  value: string | number | undefined | null,
  isSecondary?: boolean,
): string => {
  const explicit = parseExplicitBorderWidth(value);
  if (explicit !== null) return `${explicit}px`;
  return isSecondary ? BORDER_WIDTH_VAR.secondary : BORDER_WIDTH_VAR.primary;
};
